import { formFileLimits, formFileTypeWords } from './form-files';
import type {
	FormErrorCode,
	FormFieldDefinitionModel,
	FormFieldErrorCode,
} from './form-types';

/**
 * Plain default wording for the form-level codes the public endpoint returns, so a site
 * without its own messages still shows something readable (01-design.md §3). The server
 * sends them as `message`; the storefront renderer shows them in its error summary.
 */
export const FORM_ERROR_MESSAGES: Readonly<
	Record<FormErrorCode | 'validation_failed', string>
> = {
	captcha_failed: 'We could not verify this submission. Please try again.',
	captcha_expired: 'The verification check expired. Please try again.',
	origin_not_allowed: 'This site cannot submit this form.',
	form_not_found: 'This form is not available.',
	rate_limited: 'Too many submissions. Please try again later.',
	upload_session_required:
		'Please complete the verification check, then attach the file again.',
	upload_invalid: 'The file could not be sent. Please try again.',
	// PD-17590 — the same words whether the customer number exists or not.
	access_not_verified:
		"We couldn't verify those details. Check them and try again, or send your request without them and our team will follow up.",
	access_locked:
		'Too many attempts for this customer number. Please wait 15 minutes and try again, or contact your sales representative.',
	validation_failed: 'Please correct the highlighted fields.',
};

/**
 * The plain wording for a field error code (docs/claude/forms/01-design.md §3), shared by
 * the form renderer, the CRM's custom field editor and the server's refusals, so a person
 * reads the same message wherever the same value is refused. Pages wrap it in t().
 */
export function describeFormFieldError(
	field: Pick<FormFieldDefinitionModel, 'label' | 'validationRules'> &
		Partial<Pick<FormFieldDefinitionModel, 'fieldType'>>,
	code: FormFieldErrorCode,
): string {
	const rules = field.validationRules ?? {};
	const label = field.label;

	// PD-16691 — a signature is drawn, not typed, so the usual wording ("too long", "not
	// in the expected format") would not tell the visitor what to do.
	if (field.fieldType === 'signature') {
		switch (code) {
			case 'required':
				return 'Sign in the box to continue';
			case 'too_long':
				return 'The signature is too large. Clear it and sign again.';
			case 'invalid_format':
			case 'invalid_type':
				return 'The signature could not be read. Clear it and sign again.';
			default:
				break;
		}
	}

	// PD-16713 — a file question's messages name its own limits, so the visitor knows what
	// to attach instead.
	if (field.fieldType === 'file') {
		const limits = formFileLimits(field);
		switch (code) {
			case 'required':
				return `Attach a file for ${label}`;
			case 'file_type_not_allowed':
			case 'invalid_type':
				return `Attach a ${formFileTypeWords(limits.types)} file`;
			case 'file_not_valid_pdf':
				return 'This file is not a valid PDF. Save it again as a PDF and attach the new copy.';
			case 'file_not_valid_image':
				return 'This file is not a valid picture. Save it again as a JPEG, PNG or WebP and attach the new copy.';
			case 'file_too_large':
				return `Each file can be at most ${limits.maxMb} MB`;
			case 'file_empty':
				return 'The file is empty. Choose it again, or attach another copy.';
			case 'file_unreadable':
				return 'The file could not be read. Save it again and attach the new copy.';
			case 'too_many_files':
				return limits.maxFiles === 1
					? 'Attach only one file'
					: `Attach at most ${limits.maxFiles} files`;
			case 'upload_missing':
				return 'A file did not finish uploading. Remove it and attach it again.';
			default:
				break;
		}
	}

	switch (code) {
		case 'required':
			// A consent label is the whole agreement text, too long to repeat in a message.
			return field.fieldType === 'consent'
				? 'Check this box to continue'
				: `${label} is required`;
		case 'too_long':
			return rules.maxLength != null
				? `${label} must be at most ${rules.maxLength} characters`
				: `${label} is too long`;
		case 'too_short':
			return `${label} must be at least ${rules.minLength ?? 1} characters`;
		case 'below_min':
			return `${label} must be at least ${String(rules.min)}`;
		case 'above_max':
			return `${label} must be at most ${String(rules.max)}`;
		case 'invalid_number':
			return `${label} must be a number`;
		case 'invalid_email':
			return `${label} must be an email address`;
		case 'invalid_phone':
			return `${label} must be a phone number`;
		case 'invalid_url':
			return `${label} must be a web address starting with http:// or https://`;
		case 'invalid_date':
			return `${label} must be a date`;
		case 'invalid_datetime':
			return `${label} must be a date and time`;
		case 'invalid_option':
			return `${label} must be one of its listed choices`;
		case 'invalid_format':
			return `${label} is not in the expected format`;
		case 'invalid_type':
			return `${label} has a value of the wrong type`;
		default:
			return `${label} is not valid`;
	}
}
